import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2, Plus, Paperclip, Download, Smartphone, Phone, Mail, Cake, FileText, Timer, Camera } from "lucide-react";
import { toast } from "sonner";
import { api, EVENT_TYPES, EVENT_LABELS, EVENT_BADGE_STYLES, initials, photoUrl } from "@/lib/api";
import { EmployeeForm } from "@/pages/Employees";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [emp, setEmp] = useState(null);
  const [events, setEvents] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [showEvent, setShowEvent] = useState(false);
  const [eventForm, setEventForm] = useState({ date: new Date().toISOString().slice(0, 10), type: "pto", notes: "" });
  const [ots, setOts] = useState([]);
  const [otTotals, setOtTotals] = useState({ week: 0, biweek: 0 });
  const [showOT, setShowOT] = useState(false);
  const [otForm, setOtForm] = useState({ date: new Date().toISOString().slice(0, 10), hours: "", notes: "" });
  const [uploading, setUploading] = useState(false);
  const [confirm, setConfirm] = useState(null); // { title, action }
  const fileRef = useRef(null);
  const photoRef = useRef(null);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/employees/${id}/photo`, fd);
      toast.success("Profile photo updated");
      load();
    } catch {
      toast.error("Photo upload failed — image files only");
    } finally {
      if (photoRef.current) photoRef.current.value = "";
    }
  };

  const load = useCallback(async () => {
    try {
      const now = new Date();
      const monday = new Date(now);
      monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
      const fmt = (d) => d.toISOString().slice(0, 10);
      const wEnd = new Date(monday); wEnd.setDate(monday.getDate() + 6);
      const bEnd = new Date(monday); bEnd.setDate(monday.getDate() + 13);
      const [e, ev, at, ot, otW, otB] = await Promise.all([
        api.get(`/employees/${id}`),
        api.get(`/timeoff?employee_id=${id}`),
        api.get(`/employees/${id}/attachments`),
        api.get(`/overtime?employee_id=${id}`),
        api.get(`/overtime/summary?start=${fmt(monday)}&end=${fmt(wEnd)}`),
        api.get(`/overtime/summary?start=${fmt(monday)}&end=${fmt(bEnd)}`),
      ]);
      setEmp(e.data);
      setEvents(ev.data);
      setAttachments(at.data);
      setOts(ot.data);
      setOtTotals({
        week: otW.data.totals.find((t) => t.employee_id === id)?.total_hours || 0,
        biweek: otB.data.totals.find((t) => t.employee_id === id)?.total_hours || 0,
      });
    } catch {
      toast.error("Failed to load employee");
      navigate("/employees");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const logEvent = async () => {
    try {
      await api.post("/timeoff", { employee_id: id, ...eventForm });
      toast.success("Event logged");
      setShowEvent(false);
      setEventForm({ date: new Date().toISOString().slice(0, 10), type: "pto", notes: "" });
      load();
    } catch {
      toast.error("Failed to log event");
    }
  };

  const deleteEvent = async (eventId) => {
    await api.delete(`/timeoff/${eventId}`);
    toast.success("Event removed");
    load();
  };

  const saveOT = async () => {
    const hours = parseFloat(otForm.hours);
    if (!hours || hours <= 0) return toast.error("Enter valid hours");
    try {
      await api.post("/overtime", { employee_id: id, date: otForm.date, hours, notes: otForm.notes });
      toast.success("Overtime logged");
      setShowOT(false);
      setOtForm({ date: new Date().toISOString().slice(0, 10), hours: "", notes: "" });
      load();
    } catch {
      toast.error("Failed to log overtime");
    }
  };

  const deleteOT = async (otId) => {
    await api.delete(`/overtime/${otId}`);
    toast.success("Overtime entry removed");
    load();
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/employees/${id}/attachments`, fd);
      toast.success("File uploaded");
      load();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (att) => {
    try {
      const res = await api.get(`/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const removeAttachment = async (attId) => {
    await api.delete(`/attachments/${attId}`);
    toast.success("File removed");
    load();
  };

  const removeEmployee = async () => {
    await api.delete(`/employees/${id}`);
    toast.success("Employee removed");
    navigate("/employees");
  };

  if (!emp) return <div className="py-20 text-center text-sm text-slate-400" data-testid="employee-loading">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="employee-detail-page">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="icon" className="rounded-md" onClick={() => navigate("/employees")} data-testid="back-btn"><ArrowLeft className="h-4 w-4" /></Button>
        <div className="group relative shrink-0">
          <Avatar className="h-14 w-14">
            <AvatarImage src={photoUrl(emp)} alt={emp.name} />
            <AvatarFallback className="bg-[#2C5545] text-lg font-semibold text-white">{initials(emp.name)}</AvatarFallback>
          </Avatar>
          <button
            className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-[#2C5545] text-white shadow transition-colors hover:bg-[#1E3B30]"
            onClick={() => photoRef.current?.click()}
            title="Upload profile photo"
            data-testid="photo-upload-btn"
          >
            <Camera className="h-3 w-3" />
          </button>
          <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="photo-input" />
        </div>
        <div className="mr-auto">
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900" data-testid="employee-name">{emp.name}</h1>
          <p className="text-sm text-slate-500">{emp.position}{emp.employee_id_number ? ` · ID ${emp.employee_id_number}` : ""}</p>
        </div>
        <Button variant="outline" className="rounded-md" onClick={() => setShowEdit(true)} data-testid="edit-employee-btn"><Pencil className="mr-2 h-4 w-4" /> Edit</Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="rounded-md border-rose-200 text-rose-600 hover:bg-rose-50" data-testid="delete-employee-btn"><Trash2 className="mr-2 h-4 w-4" /> Remove</Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="rounded-lg">
            <AlertDialogHeader>
              <AlertDialogTitle>Remove {emp.name}?</AlertDialogTitle>
              <AlertDialogDescription>This deletes their schedule entries, time-off records and visits. This cannot be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
              <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={removeEmployee} data-testid="delete-confirm-btn">Remove</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="rounded-lg border-slate-200" data-testid="contact-card">
          <CardHeader className="pb-3"><CardTitle className="font-heading text-lg font-medium">Contact & Info</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className="flex items-center gap-2.5 text-slate-700"><Smartphone className="h-4 w-4 text-slate-400" /> <span className="text-slate-500">VITAS Cell:</span> {emp.vitas_phone || "—"}</p>
            <p className="flex items-center gap-2.5 text-slate-700"><Phone className="h-4 w-4 text-slate-400" /> <span className="text-slate-500">Personal:</span> {emp.personal_phone || "—"}</p>
            <p className="flex items-center gap-2.5 text-slate-700"><Mail className="h-4 w-4 text-slate-400" /> {emp.email || "—"}</p>
            <p className="flex items-center gap-2.5 text-slate-700"><Cake className="h-4 w-4 text-slate-400" /> {emp.birthday || "—"}</p>
            <div className="mt-2 rounded-md bg-slate-50 p-3">
              <p className="text-xs uppercase tracking-[0.15em] text-slate-500">PTO Balance</p>
              <p className="font-heading text-2xl font-semibold text-slate-900" data-testid="pto-balance">{emp.pto_balance} hrs</p>
            </div>
            {emp.notes && <p className="text-xs text-slate-500">{emp.notes}</p>}
          </CardContent>
        </Card>

        <Card className="rounded-lg border-slate-200" data-testid="events-card">
          <CardHeader className="flex-row items-center justify-between pb-3">
            <CardTitle className="font-heading text-lg font-medium">Time Off & Attendance</CardTitle>
            <Button size="sm" className="rounded-md bg-[#2C5545] text-white hover:bg-[#1E3B30]" onClick={() => setShowEvent(true)} data-testid="log-event-btn"><Plus className="mr-1 h-3.5 w-3.5" /> Log Event</Button>
          </CardHeader>
          <CardContent className="max-h-80 space-y-2 overflow-y-auto">
            {events.length === 0 && <p className="text-sm text-slate-400">No events recorded.</p>}
            {events.map((ev) => (
              <div key={ev.id} className="flex items-center justify-between rounded-md border border-slate-100 p-2.5" data-testid={`event-${ev.id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={EVENT_BADGE_STYLES[ev.type]}>{EVENT_LABELS[ev.type]}</Badge>
                    <span className="text-xs font-medium text-slate-700">{ev.date}</span>
                  </div>
                  {ev.notes && <p className="mt-1 text-xs text-slate-500">{ev.notes}</p>}
                </div>
                <button className="p-1 text-slate-300 transition-colors hover:text-rose-600" onClick={() => setConfirm({ title: "Delete this event?", action: () => deleteEvent(ev.id) })} data-testid={`delete-event-${ev.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="rounded-lg border-slate-200" data-testid="overtime-card">
          <CardHeader className="flex-row items-center justify-between pb-3">
            <CardTitle className="font-heading flex items-center gap-2 text-lg font-medium"><Timer className="h-4 w-4" /> Overtime</CardTitle>
            <Button size="sm" className="rounded-full bg-[#2C5545] text-white hover:bg-[#1E3B30]" onClick={() => setShowOT(true)} data-testid="log-ot-btn"><Plus className="mr-1 h-3.5 w-3.5" /> Log OT</Button>
          </CardHeader>
          <CardContent className="max-h-80 space-y-2 overflow-y-auto">
            <div className="mb-3 grid grid-cols-2 gap-2">
              <div className="rounded-xl bg-[#FFECE5] p-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#9F1239]">This Week</p>
                <p className="font-heading text-xl font-semibold text-[#9F1239]" data-testid="ot-week-total">{otTotals.week}h</p>
              </div>
              <div className="rounded-xl bg-[#FFECE5] p-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#9F1239]">Biweekly</p>
                <p className="font-heading text-xl font-semibold text-[#9F1239]" data-testid="ot-biweek-total">{otTotals.biweek}h</p>
              </div>
            </div>
            <p className="text-[10px] text-slate-400">Weekend shifts (8h) and on-call hours count automatically. Log after-5pm hours manually.</p>
            {ots.length === 0 && <p className="text-sm text-slate-400">No manual overtime logged.</p>}
            {ots.map((o) => (
              <div key={o.id} className="flex items-center justify-between rounded-md border border-slate-100 p-2.5" data-testid={`ot-entry-${o.id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="rounded border border-[#FECDD3] bg-[#FFECE5] px-1.5 py-px text-[10px] font-bold text-[#9F1239]">{o.hours}h OT</span>
                    <span className="text-xs font-medium text-slate-700">{o.date}</span>
                  </div>
                  {o.notes && <p className="mt-1 text-xs text-slate-500">{o.notes}</p>}
                </div>
                <button className="p-1 text-slate-300 transition-colors hover:text-rose-600" onClick={() => setConfirm({ title: "Delete this overtime entry?", action: () => deleteOT(o.id) })} data-testid={`delete-ot-${o.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="rounded-lg border-slate-200" data-testid="attachments-card">
          <CardHeader className="flex-row items-center justify-between pb-3">
            <CardTitle className="font-heading text-lg font-medium">Files & Documents</CardTitle>
            <Button size="sm" variant="outline" className="rounded-md" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-file-btn">
              <Paperclip className="mr-1 h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Attach"}
            </Button>
            <input ref={fileRef} type="file" className="hidden" onChange={upload} data-testid="file-input" />
          </CardHeader>
          <CardContent className="max-h-80 space-y-2 overflow-y-auto">
            {attachments.length === 0 && <p className="text-sm text-slate-400">No files attached. Upload certifications, licenses, etc.</p>}
            {attachments.map((att) => (
              <div key={att.id} className="flex items-center gap-2.5 rounded-md border border-slate-100 p-2.5" data-testid={`attachment-${att.id}`}>
                <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-slate-700">{att.original_filename}</p>
                  <p className="text-[10px] text-slate-400">{att.size < 1024 ? `${att.size} B` : `${Math.round(att.size / 1024)} KB`}</p>
                </div>
                <button className="p-1 text-slate-400 transition-colors hover:text-slate-900" onClick={() => download(att)} data-testid={`download-${att.id}`}><Download className="h-3.5 w-3.5" /></button>
                <button className="p-1 text-slate-300 transition-colors hover:text-rose-600" onClick={() => setConfirm({ title: `Remove ${att.original_filename}?`, action: () => removeAttachment(att.id) })} data-testid={`remove-${att.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <EmployeeForm open={showEdit} onOpenChange={setShowEdit} onSaved={load} employeeId={id} initial={emp} />

      <Dialog open={showEvent} onOpenChange={setShowEvent}>
        <DialogContent className="rounded-lg sm:max-w-md" data-testid="event-dialog">
          <DialogHeader><DialogTitle className="font-heading">Log Time Off / Attendance</DialogTitle><DialogDescription>Record PTO, leave, late entry, early out or a call-off for this employee.</DialogDescription></DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Type</Label>
              <Select value={eventForm.type} onValueChange={(v) => setEventForm({ ...eventForm, type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="event-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`event-type-${t.value}`}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="event-date">Date</Label>
              <Input id="event-date" type="date" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} className="mt-1.5" data-testid="event-date-input" />
            </div>
            <div>
              <Label htmlFor="event-notes">Notes</Label>
              <Input id="event-notes" value={eventForm.notes} onChange={(e) => setEventForm({ ...eventForm, notes: e.target.value })} className="mt-1.5" data-testid="event-notes-input" placeholder="Optional details" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-md" onClick={() => setShowEvent(false)} data-testid="event-cancel-btn">Cancel</Button>
              <Button className="rounded-md bg-[#2C5545] text-white hover:bg-[#1E3B30]" onClick={logEvent} data-testid="event-save-btn">Log Event</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showOT} onOpenChange={setShowOT}>
        <DialogContent className="rounded-lg sm:max-w-md" data-testid="ot-dialog">
          <DialogHeader><DialogTitle className="font-heading">Log Overtime Hours</DialogTitle><DialogDescription>Hours worked after 5pm on weekdays. Weekend and on-call hours are counted automatically.</DialogDescription></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ot-date">Date</Label>
                <Input id="ot-date" type="date" value={otForm.date} onChange={(e) => setOtForm({ ...otForm, date: e.target.value })} className="mt-1.5" data-testid="ot-date-input" />
              </div>
              <div>
                <Label htmlFor="ot-hours">Hours</Label>
                <Input id="ot-hours" type="number" step="0.25" min="0" value={otForm.hours} onChange={(e) => setOtForm({ ...otForm, hours: e.target.value })} className="mt-1.5" placeholder="e.g. 2.5" data-testid="ot-hours-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="ot-notes">Notes</Label>
              <Input id="ot-notes" value={otForm.notes} onChange={(e) => setOtForm({ ...otForm, notes: e.target.value })} className="mt-1.5" placeholder="e.g. Stayed until 7:30pm" data-testid="ot-notes-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-md" onClick={() => setShowOT(false)} data-testid="ot-cancel-btn">Cancel</Button>
              <Button className="rounded-full bg-[#2C5545] text-white hover:bg-[#1E3B30]" onClick={saveOT} data-testid="ot-save-btn">Log OT</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="rounded-lg" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => { confirm?.action(); setConfirm(null); }} data-testid="confirm-action-btn">Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
